import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  getDocs,
  DocumentData,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { db, storage } from '../../firebase';
import { EmergencyContact, User } from '../../types';
import { ChildcareMessage, CheckInRecord } from './types';

export const useFamily = (familyId: string) => {
  const [family, setFamily] = useState<User[]>([]);

  useEffect(() => {
    if (!familyId) return;

    const familyRef = collection(db, 'users');
    const q = query(familyRef, where('familyId', '==', familyId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const familyData: User[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        familyData.push({
          ...data,
          userId: docSnap.id,
          userType: data.userType
            ? data.userType
            : data.spouseId
            ? 'spouse'
            : 'other',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.toDate() : null,
        } as unknown as User);
      });
      setFamily(familyData);
    });

    return () => unsubscribe();
  }, [familyId]);

  return family;
};

const parseCheckInDoc = (
  docSnap: DocumentData,
  docId: string
): CheckInRecord => {
  const data = docSnap;
  return {
    id: docId,
    parentId: data.parentId,
    familyId: data.familyId,
    childId: data.childId,
    childFirstName: data.childFirstName,
    childLastName: data.childLastName,
    dateOfBirth: data.dateOfBirth?.toDate?.() || null,
    photoUrl: data.photoUrl,
    allergies: data.allergies,
    specialNotes: data.specialNotes,
    parentName: data.parentName,
    parentPhone: data.parentPhone,
    emergencyContacts: data.emergencyContacts,
    isWalkIn: data.isWalkIn || false,
    pickupPin: data.pickupPin,
    checkInTime: data.checkInTime?.toDate?.() || new Date(),
    checkOutTime: data.checkOutTime?.toDate?.() || null,
    createdOn: data.createdOn?.toDate?.() || new Date(),
  };
};

export const useFamilyCheckin = (familyId: string) => {
  const [checkins, setCheckins] = useState<CheckInRecord[]>([]);

  useEffect(() => {
    if (!familyId) return;

    const checkinsRef = collection(db, 'childCheckIns');
    const q = query(
      checkinsRef,
      where('familyId', '==', familyId),
      orderBy('createdOn', 'desc'),
      limit(20)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const checkinData: CheckInRecord[] = [];
      snapshot.forEach((docSnap) => {
        checkinData.push(parseCheckInDoc(docSnap.data(), docSnap.id));
      });
      setCheckins(checkinData);
    });

    return () => unsubscribe();
  }, [familyId]);

  return { checkins };
};

export const useAllActiveCheckins = (active: boolean) => {
  const [checkins, setCheckins] = useState<CheckInRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = active
      ? query(
          collection(db, 'childCheckIns'),
          where('checkOutTime', '==', null),
          orderBy('createdOn', 'desc'),
          limit(200)
        )
      : query(
          collection(db, 'childCheckIns'),
          orderBy('createdOn', 'desc'),
          limit(200)
        );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data: CheckInRecord[] = [];
      snapshot.forEach((docSnap) => {
        data.push(parseCheckInDoc(docSnap.data(), docSnap.id));
      });
      setCheckins(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [active]);

  return { checkins, loading };
};

export const checkOutChild = async (checkinId: string): Promise<void> => {
  await updateDoc(doc(db, 'childCheckIns', checkinId), {
    checkOutTime: serverTimestamp(),
  });
};

export interface WalkInData {
  childFirstName: string;
  childLastName: string;
  dateOfBirth?: Date | null;
  photoUrl?: string | null;
  allergies?: string;
  specialNotes?: string;
  parentName?: string;
  parentPhone?: string;
  emergencyContacts?: EmergencyContact[];
  adminId: string;
  pickupPin?: string;
}

export const walkInCheckIn = async (data: WalkInData) => {
  const pickupPin =
    data.pickupPin ?? Math.floor(1000 + Math.random() * 9000).toString();
  const thedata = {
    parentId: 'walk-in',
    childId: 'walk-in-' + Date.now(),
    familyId: 'walk-in-' + Date.now(),
    childFirstName: data.childFirstName,
    childLastName: data.childLastName,
    dateOfBirth: data.dateOfBirth ?? null,
    photoUrl: data.photoUrl ?? null,
    allergies: data.allergies || null,
    specialNotes: data.specialNotes || null,
    parentName: data.parentName || null,
    parentPhone: data.parentPhone || null,
    emergencyContacts: data.emergencyContacts ?? [],
    pickupPin,
    isWalkIn: true,
    checkInTime: serverTimestamp(),
    checkOutTime: null,
    createdOn: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'childCheckIns'), thedata);

  return { id: docRef.id, ...thedata };
};

export const checkInChild = async (
  child: User,
  parentId: string,
  parentName: string,
  familyId: string,
  pickupPin?: string
): Promise<{ id: string; pickupPin: string }> => {
  const pin = pickupPin ?? Math.floor(1000 + Math.random() * 9000).toString();
  const docRef = await addDoc(collection(db, 'childCheckIns'), {
    parentId,
    childId: child.userId,
    childFirstName: child.firstName,
    childLastName: child.lastName,
    dateOfBirth: child.dateOfBirth || null,
    photoUrl: child.profilePic || null,
    allergies: child.allergies || null,
    specialNotes: child.specialNotes || null,
    parentName: parentName || null,
    parentPhone: child.phone || null,
    emergencyContacts: child.emergencyContacts || [],
    pickupPin: pin,
    checkInTime: serverTimestamp(),
    checkOutTime: null,
    createdOn: serverTimestamp(),
    familyId,
  });
  return { id: docRef.id, pickupPin: pin };
};

export const uploadPhoto = async (dataUrl: string): Promise<string> => {
  const storageRef = ref(storage, `profile-pics/${uuidv4()}.jpg`);
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const result = await uploadBytes(storageRef, blob, {
    contentType: 'image/jpeg',
  });
  return getDownloadURL(result.ref);
};

export interface ChildFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  phone: string;
  profilePic: string | null;
  allergies: string;
  specialNotes: string;
  emergencyContacts: { name: string; phone: string }[];
}

export const saveChild = async (
  data: ChildFormData,
  familyId: string,
  parentId: string,
  email: string,
  childId?: string
): Promise<void> => {
  let profilePic = data.profilePic;
  if (profilePic && !profilePic.startsWith('http')) {
    profilePic = await uploadPhoto(profilePic);
  }

  const payload = {
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    firstName_lc: data.firstName.trim().toLowerCase(),
    lastName_lc: data.lastName.trim().toLowerCase(),
    dateOfBirth: data.dateOfBirth ?? null,
    phone: data.phone.trim() || null,
    parentPhone: data.phone.trim() || null,
    profilePic: profilePic ?? null,
    allergies: data.allergies.trim() || null,
    specialNotes: data.specialNotes.trim() || null,
    emergencyContacts: data.emergencyContacts.filter(
      (c) => c.name.trim() || c.phone.trim()
    ),
    userType: 'child',
    familyId,
    parentId,
    email,
  };

  if (childId) {
    await updateDoc(doc(db, 'users', childId), payload);
  } else {
    await addDoc(collection(db, 'users'), payload);
  }
};

export const deleteChild = async (childId: string): Promise<void> => {
  await deleteDoc(doc(db, 'users', childId));
};

// ── Messaging ────────────────────────────────────────────────────────────────

export const sendCheckinMessage = async (
  msg: Omit<ChildcareMessage, 'id' | 'createdAt'>
): Promise<void> => {
  await addDoc(collection(db, 'checkinMessages'), {
    ...msg,
    createdAt: serverTimestamp(),
  });
};

export const useCheckinMessages = (childId: string, familyId: string) => {
  const [messages, setMessages] = useState<ChildcareMessage[]>([]);

  useEffect(() => {
    if (!childId || !familyId) return;
    const q = query(
      collection(db, 'checkinMessages'),
      where('childId', '==', childId),
      where('familyId', '==', familyId),
      orderBy('createdAt', 'asc'),
      limit(200)
    );
    return onSnapshot(q, (snap) => {
      setMessages(
        snap.docs.map((d) => ({
          ...(d.data() as Omit<ChildcareMessage, 'id' | 'createdAt'>),
          id: d.id,
          createdAt: d.data().createdAt?.toDate() ?? new Date(),
        }))
      );
    });
  }, [childId, familyId]);

  return messages;
};

export const markCheckinMessagesRead = async (
  childId: string,
  familyId: string,
  role: 'admin' | 'parent'
): Promise<void> => {
  const field = role === 'admin' ? 'readByAdmin' : 'readByParent';
  const oppositeRole = role === 'admin' ? 'parent' : 'admin';
  const q = query(
    collection(db, 'checkinMessages'),
    where('childId', '==', childId),
    where('familyId', '==', familyId),
    where('senderRole', '==', oppositeRole),
    where(field, '==', false)
  );
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { [field]: true })));
};

// Single subscription for all admin unread messages — group by childId in UI
export const useAdminUnreadMessages = () => {
  const [msgs, setMsgs] = useState<ChildcareMessage[]>([]);

  useEffect(() => {
    const q = query(
      collection(db, 'checkinMessages'),
      where('senderRole', '==', 'parent'),
      where('readByAdmin', '==', false)
    );
    return onSnapshot(q, (snap) => {
      setMsgs(
        snap.docs.map((d) => ({
          ...(d.data() as Omit<ChildcareMessage, 'id' | 'createdAt'>),
          id: d.id,
          createdAt: d.data().createdAt?.toDate() ?? new Date(),
        }))
      );
    });
  }, []);

  return msgs;
};

export const useParentUnreadCount = (childId: string, familyId: string) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!childId || !familyId || childId.startsWith('walk-in')) return;
    const q = query(
      collection(db, 'checkinMessages'),
      where('childId', '==', childId),
      where('familyId', '==', familyId),
      where('senderRole', '==', 'admin'),
      where('readByParent', '==', false)
    );
    return onSnapshot(q, (snap) => setCount(snap.size));
  }, [childId, familyId]);

  return count;
};
